import React from 'react';
import { Box, Typography } from '@mui/material';
import { NutritionReportSectionTable, type NutritionSection, type ToneStyles } from './NutritionReportSectionTable';

interface NutritionReportPdfViewProps {
  title: string;
  subtitle: string;
  sections: NutritionSection[];
  toneStyles: ToneStyles;
  macroDistributionBlock?: React.ReactNode;
  flagged: number;
  borderline: number;
  met: number;
  /** Meal-plan `nutrition` fields absent from the API payload for Nutrient Report detail rows. */
  mealPlanReportMissingLabels?: string[];
}

export const NutritionReportPdfView: React.FC<NutritionReportPdfViewProps> = ({
  title,
  subtitle,
  sections,
  toneStyles,
  macroDistributionBlock,
  flagged,
  borderline,
  met,
  mealPlanReportMissingLabels = [],
}) => {
  const energySection = sections.find((section) => section.id === 'energy');
  const remainingSections = sections.filter((section) => section.id !== 'energy');
  const macronutrientsSection = remainingSections.find((section) => section.id === 'macronutrients');
  const sectionsAfterMacronutrientsTable = remainingSections.filter((section) => section.id !== 'macronutrients');

  return (
    <Box
      sx={{
        width: '100%',
        pt: 1.5,
        pb: 3,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 1060, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '22px',
              fontWeight: 700,
              color: '#1e2939',
              lineHeight: 1.2,
              mb: 1,
              letterSpacing: '-0.5px',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              color: '#667085',
              lineHeight: 1.4,
              fontWeight: 400,
            }}
          >
            {subtitle}
          </Typography>
          {mealPlanReportMissingLabels.length > 0 ? (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                color: '#667085',
                lineHeight: 1.45,
                mt: 1,
              }}
            >
              Not reported in this meal plan analysis: {mealPlanReportMissingLabels.join(', ')}.
            </Typography>
          ) : null}
        </Box>

        <Box
          data-pdf-block="true"
          sx={{
            display: 'flex',
            gap: 1.5,
            mb: 3,
            width: '100%',
          }}
        >
          {[
            { label: 'Warnings', value: flagged, bg: '#FEF3F2', color: '#D92D20' },
            { label: 'Cautions', value: borderline, bg: '#FEF0C7', color: '#B54708' },
            { label: 'Met', value: met, bg: '#ECFDF3', color: '#027A48' },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                flex: 1,
                bgcolor: item.bg,
                py: 1.2,
                height: '50px',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: item.color,
                }}
              >
                {item.value} {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {energySection ? (
            <Box
              key={energySection.id}
              data-pdf-block="true"
              data-pdf-avoid-split="true"
              sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
            >
              <NutritionReportSectionTable
                section={energySection}
                toneStyles={toneStyles}
                mode="nutrition-report"
                hideExpandColumn
              />
            </Box>
          ) : null}

          {/** One PDF block: Macronutrient Analysis (table + pie) first, then Macronutrients nutrient-report table. */}
          {macronutrientsSection || macroDistributionBlock ? (
            <Box
              data-pdf-block="true"
              data-pdf-avoid-split="true"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
              }}
            >
              {macroDistributionBlock ? (
                <Box sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>{macroDistributionBlock}</Box>
              ) : null}
              {macronutrientsSection ? (
                <Box key={macronutrientsSection.id} sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                  <NutritionReportSectionTable
                    section={macronutrientsSection}
                    toneStyles={toneStyles}
                    mode="nutrition-report"
                    hideExpandColumn
                  />
                </Box>
              ) : null}
            </Box>
          ) : null}

          {sectionsAfterMacronutrientsTable.map((section) => (
            <Box
              key={section.id}
              data-pdf-block="true"
              data-pdf-avoid-split="true"
              sx={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
            >
              <NutritionReportSectionTable
                section={section}
                toneStyles={toneStyles}
                mode="nutrition-report"
                hideExpandColumn
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
